import {
	AskedChangeFieldEnum,
	type WorkerAskedChangesResponse,
} from '@/modules/worker/components/workerAskChanges/types';

import type { WorkerDetail } from '@/modules/worker/types';

export const normalizeWorkerJobPayload = (
	raw: unknown
): { jobId: string; experience?: string; degreeLevel?: string } | null => {
	if (!raw || typeof raw !== 'object') return null;
	const record = raw as Record<string, unknown>;
	const jobId =
		typeof record.jobId === 'string'
			? record.jobId
			: typeof record.id === 'string'
				? record.id
				: null;
	if (!jobId) return null;
	return {
		jobId,
		experience:
			typeof record.experience === 'string' ? record.experience : undefined,
		degreeLevel:
			typeof record.degreeLevel === 'string' ? record.degreeLevel : undefined,
	};
};

export const normalizeWorkerJobsArray = (
	value: unknown
): { jobId: string; experience?: string; degreeLevel?: string }[] => {
	if (!Array.isArray(value)) return [];
	return value
		.map(normalizeWorkerJobPayload)
		.filter((j): j is NonNullable<typeof j> => j !== null);
};

/**
 * Whether the UI should show the pending-changes alert (banner).
 * Mirrors legacy behaviour: a single pending gender-only change does not surface the alert.
 */
export const shouldShowAskedChangesAlert = (
	response: WorkerAskedChangesResponse
): boolean => {
	const { changes } = response;
	if (!changes.length) return false;
	if (changes.length === 1 && changes[0].field === AskedChangeFieldEnum.GENDER)
		return false;
	return true;
};

const isWorkerAskedChangesResponseShape = (
	value: unknown
): value is WorkerAskedChangesResponse => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
	const record = value as Record<string, unknown>;
	return Array.isArray(record.changes);
};

/**
 * Legacy map shape from GET worker (field → proposed payload). Single gender-only change does not surface the alert.
 */
const shouldShowAlertFromLegacyAskedChangesMap = (
	askedChanges: unknown
): boolean => {
	if (
		!askedChanges ||
		typeof askedChanges !== 'object' ||
		Array.isArray(askedChanges)
	)
		return false;
	const record = askedChanges as Record<string, unknown>;
	const keys = Object.keys(record);
	if (keys.length === 0) return false;
	if (record.gender && keys.length === 1) return false;
	return true;
};

/**
 * Whether to show the pending-changes banner using only the worker payload from GET worker (no asked-changes route).
 */
export const shouldShowAskedChangesAlertFromWorker = (
	worker: WorkerDetail
): boolean => {
	const raw = worker.askedChanges;
	if (raw == null) return false;
	if (isWorkerAskedChangesResponseShape(raw)) {
		return shouldShowAskedChangesAlert(raw);
	}
	return shouldShowAlertFromLegacyAskedChangesMap(raw);
};
